/*
 * Size Finder
 * ------------------------------------------------------------
 * Height/weight -> recommended gi size, per the official Inverted Gear
 * sizing charts (A-series adult, F-series women's, M-series kids).
 *
 * Standalone: no jQuery. The open/close of the surrounding modal is
 * handled by the theme's own delegated [data-action=open-modal] handler
 * in california.js, so this file only owns what's inside the modal.
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  /* Heights stored as inches, weights as pounds — the form converts
     metric input before it ever reaches the matcher. Ranges are taken
     verbatim from the sizing charts; see NOTES below for the two places
     where the source charts disagree. */
  var CHARTS = {
    a: {
      label: "Men's / Adult",
      code: 'A',
      sizes: [
        { size: 'A0',  h: [60, 64], w: [110, 145] },
        { size: 'A0H', h: [60, 64], w: [140, 170], fit: 'roomy' },
        { size: 'A1',  h: [64, 68], w: [130, 165] },
        { size: 'A1S', h: [68, 71], w: [130, 165], fit: 'slim' },
        { size: 'A1H', h: [64, 68], w: [160, 190], fit: 'roomy' },
        { size: 'A2',  h: [68, 71], w: [165, 195] },
        { size: 'A2S', h: [71, 74], w: [160, 190], fit: 'slim' },
        { size: 'A2H', h: [68, 71], w: [225, 260], fit: 'roomy' },
        { size: 'A3',  h: [71, 74], w: [195, 225] },
        { size: 'A3S', h: [73, 76], w: [190, 230], fit: 'slim' },
        { size: 'A3H', h: [71, 74], w: [245, 285], fit: 'roomy' },
        { size: 'A4',  h: [73, 76], w: [230, 250] },
        { size: 'A5',  h: [76, 78], w: [250, 280] }
      ]
    },
    f: {
      label: "Women's",
      code: 'F',
      /* NOTE: the printed chart and the internal spreadsheet give slightly
         different height bands for F0-F4 (the weight bands agree). We use the
         union of the two so nobody gets pushed out of a size on a technicality,
         and lean on weight as the primary signal below. */
      sizes: [
        { size: 'F0', h: [56, 64], w: [90, 115] },
        { size: 'F1', h: [60, 66], w: [110, 145] },
        { size: 'F2', h: [64, 68], w: [130, 165] },
        { size: 'F3', h: [66, 71], w: [165, 195] },
        { size: 'F4', h: [70, 74], w: [195, 235] },
        { size: 'F5', h: [73, 76], w: [230, 270] }
      ]
    },
    m: {
      label: 'Kids',
      code: 'M',
      sizes: [
        { size: 'M00', h: [45, 48], w: [0, 50] },
        { size: 'M0',  h: [48, 51], w: [50, 65] },
        { size: 'M1',  h: [51, 54], w: [65, 80] },
        { size: 'M2',  h: [54, 57], w: [80, 95] },
        { size: 'M3',  h: [57, 60], w: [95, 110] }
      ]
    }
  };

  var FIT_NOTES = {
    roomy: 'Cut with more room through the jacket body, hips and thighs.',
    slim:  'Slim cut — longer in the sleeve and leg, trimmer through the body.'
  };

  /* How far outside a range a measurement sits, in that range's own units. */
  function distanceOutside(value, range) {
    if (value >= range[0] && value <= range[1]) return 0;
    return value < range[0] ? range[0] - value : value - range[1];
  }

  /* Weight is the primary signal and height the tiebreaker — that ordering
     comes from the charts themselves, where the height bands overlap far more
     than the weight bands do. Penalties are normalised (10lb ~ 2in) so the
     two are comparable, then weight is doubled to keep it dominant. */
  function score(entry, heightIn, weightLb) {
    var wOff = distanceOutside(weightLb, entry.w);
    var hOff = distanceOutside(heightIn, entry.h);
    return {
      total: (wOff / 10) * 2 + (hOff / 2),
      weightOff: wOff,
      heightOff: hOff,
      weightIn: wOff === 0,
      heightIn: hOff === 0
    };
  }

  /* Where in its range a value sits, 0 = bottom, 1 = top. */
  function positionInRange(value, range) {
    if (range[1] === range[0]) return 0.5;
    var p = (value - range[0]) / (range[1] - range[0]);
    return Math.max(0, Math.min(1, p));
  }

  function recommend(seriesKey, heightIn, weightLb) {
    var chart = CHARTS[seriesKey];
    var ranked = chart.sizes.map(function (entry) {
      var s = score(entry, heightIn, weightLb);
      return {
        entry: entry,
        score: s,
        /* Prefer a plain size over a slim/roomy cut when they tie, so we
           only steer someone to a specialty cut when it genuinely fits better. */
        tiebreak: entry.fit ? 1 : 0,
        /* The weight bands overlap in places — most of all on the women's
           chart, where two sizes can both fully contain the same person.
           Sitting mid-band beats sitting at the very edge of one. */
        offCentre: Math.abs(positionInRange(weightLb, entry.w) - 0.5)
      };
    });

    ranked.sort(function (a, b) {
      if (a.score.total !== b.score.total) return a.score.total - b.score.total;
      if (a.tiebreak !== b.tiebreak) return a.tiebreak - b.tiebreak;
      return a.offCentre - b.offCentre;
    });

    var best = ranked[0];
    var fullMatches = ranked.filter(function (r) {
      return r.score.weightIn && r.score.heightIn;
    });

    var confidence;
    if (fullMatches.length > 1) confidence = 'overlap';
    else if (best.score.weightIn && best.score.heightIn) confidence = 'strong';
    else if (best.score.total <= 1) confidence = 'close';
    else confidence = 'borderline';

    /* Kids at the very top of a band outgrow it fast — the chart is a
       snapshot and a gi is not. */
    var sizeUpHint = null;
    if (seriesKey === 'm' && confidence !== 'borderline') {
      var topOfWeight = positionInRange(weightLb, best.entry.w) >= 0.85;
      var topOfHeight = positionInRange(heightIn, best.entry.h) >= 0.85;
      if (topOfWeight || topOfHeight) {
        var next = chart.sizes[chart.sizes.indexOf(best.entry) + 1];
        if (next) sizeUpHint = next.size;
      }
    }

    return {
      chart: chart,
      best: best,
      confidence: confidence,
      sizeUpHint: sizeUpHint,
      runnerUp: ranked[1],
      alternates: ranked.slice(1, 3)
    };
  }

  /* ---------- formatting helpers ---------- */

  function formatHeight(inches) {
    var ft = Math.floor(inches / 12);
    var inch = Math.round(inches - ft * 12);
    if (inch === 12) { ft += 1; inch = 0; }
    return ft + "'" + inch + '"';
  }

  function formatHeightRange(range) {
    return formatHeight(range[0]) + '–' + formatHeight(range[1]);
  }

  function formatWeightRange(range) {
    if (range[0] === 0) return 'up to ' + range[1] + ' lbs';
    return range[0] + '–' + range[1] + ' lbs';
  }

  /* ---------- matching a recommendation to a real variant ---------- */

  /*
   * Size options on these products carry their stock state in the option
   * value itself — "A2--SHIPS-NOW--", "A2--PRE-ORDER--", "A3S--SOLD-OUT--".
   * (Both "SOLD-OUT" and "SOLD--OUT" appear in the catalogue, hence the
   * loose marker parsing.) Older products use a bare "A2" with no marker.
   */
  function parseOptionValue(raw) {
    var text = String(raw || '').trim();
    var split = text.indexOf('--');
    var code = split === -1 ? text : text.slice(0, split).trim();
    var marker = split === -1 ? '' : text.slice(split).replace(/-/g, ' ').trim().toUpperCase();

    var status = '';
    if (marker.indexOf('SOLD') !== -1) status = 'sold-out';
    else if (marker.indexOf('PRE') !== -1) status = 'pre-order';
    else if (marker.indexOf('SHIP') !== -1) status = 'ships-now';

    return { raw: text, code: code, status: status };
  }

  /* "A2" must not match "A2S" or "A2H" — the letter suffix is a different size. */
  function codeMatches(optionCode, sizeCode) {
    return optionCode.toUpperCase() === sizeCode.toUpperCase();
  }

  /* Among sizes the customer can actually buy, prefer one shipping today. */
  var STATUS_RANK = { 'ships-now': 0, '': 1, 'pre-order': 2 };

  function optionNames(product) {
    return (product.options || []).map(function (o) {
      return String(o && o.name ? o.name : o);
    });
  }

  function sizeOptionIndex(product) {
    var names = optionNames(product);
    for (var i = 0; i < names.length; i++) {
      if (names[i].toLowerCase().replace(/[^a-z0-9]/g, '') === 'size') return i;
    }
    return -1;
  }

  /*
   * The --SHIPS-NOW--/--PRE-ORDER-- suffix is a merchandising label, not a
   * stock check. On Panda 400, A2--SHIPS-NOW-- exists only in a colour that
   * is sold out, while A2--PRE-ORDER-- is buyable in four. So availability
   * is read from the product JSON and the suffix is used only to describe
   * what the customer is getting.
   */
  function chooseVariant(product, sizeCode, current) {
    var sizeIndex = sizeOptionIndex(product);
    if (sizeIndex === -1) return null;

    var key = 'option' + (sizeIndex + 1);
    var candidates = (product.variants || []).filter(function (v) {
      return codeMatches(parseOptionValue(v[key]).code, sizeCode);
    });
    if (!candidates.length) return null;

    var available = candidates.filter(function (v) { return v.available; });

    function byStatus(list) {
      return list.slice().sort(function (a, b) {
        var sa = STATUS_RANK[parseOptionValue(a[key]).status];
        var sb = STATUS_RANK[parseOptionValue(b[key]).status];
        return (sa === undefined ? 1 : sa) - (sb === undefined ? 1 : sb);
      });
    }

    /* Hold on to whatever else they already picked, if anything fits. */
    var keepsOthers = available.filter(function (v) {
      for (var i = 0; i < 3; i++) {
        if (i === sizeIndex) continue;
        var chosen = current[i];
        if (chosen && v['option' + (i + 1)] !== chosen) return false;
      }
      return true;
    });

    var pick = byStatus(keepsOthers)[0] || byStatus(available)[0] || null;

    var changed = [];
    if (pick) {
      var names = optionNames(product);
      for (var n = 0; n < names.length; n++) {
        if (n === sizeIndex) continue;
        var was = current[n];
        var now = pick['option' + (n + 1)];
        if (was && now && was !== now) changed.push({ name: names[n], from: was, to: now });
      }
    }

    return {
      sizeIndex: sizeIndex,
      candidates: candidates,
      available: available,
      pick: pick,
      status: pick ? parseOptionValue(pick[key]).status : '',
      changed: changed
    };
  }

  SizeFinder.prototype.sectionEl = function () {
    var section = this.section;
    if ((!section || !document.contains(section)) && this.sectionId) {
      section = document.getElementById('shopify-section-' + this.sectionId);
    }
    return section && document.contains(section) ? section : null;
  };

  /* Scope to the theme's own variant picker — app blocks such as the Rebuy
     bundle widget render their own size selects inside this same section. */
  SizeFinder.prototype.variantWraps = function () {
    var section = this.sectionEl();
    if (!section) return [];
    var picker = section.querySelector('.block.type-variant_picker');
    if (!picker) return [];
    return Array.prototype.slice.call(picker.querySelectorAll('.variant-input-wrap'));
  };

  SizeFinder.prototype.productData = function () {
    var section = this.sectionEl();
    var el = section && section.querySelector('[id^="ProductJson-"]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };

  function wrapIndex(wrap) {
    return parseInt(String(wrap.getAttribute('data-index') || '').replace('option', ''), 10);
  }

  SizeFinder.prototype.currentSelections = function () {
    var current = [];
    this.variantWraps().forEach(function (wrap) {
      var idx = wrapIndex(wrap);
      if (!idx) return;
      if (wrap.getAttribute('data-type') === 'dropdown') {
        var select = wrap.querySelector('select');
        if (select) current[idx - 1] = select.value;
      } else {
        var checked = wrap.querySelector('input:checked');
        if (checked) current[idx - 1] = checked.value;
      }
    });
    return current;
  };

  SizeFinder.prototype.applyVariant = function (variant, sizeIndex) {
    var wraps = this.variantWraps();
    if (!wraps.length) return false;

    /* Set the other options first so size lands on a settled combination. */
    wraps.sort(function (a, b) {
      return ((wrapIndex(a) - 1) === sizeIndex ? 1 : 0) - ((wrapIndex(b) - 1) === sizeIndex ? 1 : 0);
    });

    wraps.forEach(function (wrap) {
      var idx = wrapIndex(wrap);
      if (!idx) return;
      var want = variant['option' + idx];
      if (want == null) return;

      if (wrap.getAttribute('data-type') === 'dropdown') {
        var select = wrap.querySelector('select');
        if (select && select.value !== want) {
          select.value = want;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      var inputs = wrap.querySelectorAll('.variant-input');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].getAttribute('data-value') === want) {
          var radio = inputs[i].querySelector('input');
          if (radio && !radio.checked) radio.click();
          break;
        }
      }
    });

    return true;
  };

  /* ---------- DOM wiring ---------- */

  function SizeFinder(root) {
    this.root = root;
    /* The theme's modal handler moves the whole modal to document.body when
       it opens, which takes this element out of its section. Resolve the
       section now, while the markup is still where Liquid rendered it. */
    this.section = root.closest('.shopify-section');
    this.sectionId = root.getAttribute('data-section-id') || '';
    this.series = root.getAttribute('data-default-series') || 'ask';
    this.unit = 'imperial';
    this.bind();
    this.reset();
  }

  SizeFinder.prototype.$ = function (sel) {
    return this.root.querySelector(sel);
  };

  SizeFinder.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (e) {
      var seriesBtn = e.target.closest('[data-sf-series]');
      if (seriesBtn && self.root.contains(seriesBtn)) {
        e.preventDefault();
        self.series = seriesBtn.getAttribute('data-sf-series');
        self.goToStep('measure');
        return;
      }

      var unitBtn = e.target.closest('[data-sf-unit]');
      if (unitBtn && self.root.contains(unitBtn)) {
        e.preventDefault();
        self.setUnit(unitBtn.getAttribute('data-sf-unit'));
        return;
      }

      var backBtn = e.target.closest('[data-sf-back]');
      if (backBtn && self.root.contains(backBtn)) {
        e.preventDefault();
        self.goToStep(backBtn.getAttribute('data-sf-back'));
        return;
      }

      var restartBtn = e.target.closest('[data-sf-restart]');
      if (restartBtn && self.root.contains(restartBtn)) {
        e.preventDefault();
        self.reset();
        return;
      }

      var selectBtn = e.target.closest('[data-sf-select-variant]');
      if (selectBtn && self.root.contains(selectBtn)) {
        e.preventDefault();
        if (self.currentChoice &&
            self.applyVariant(self.currentChoice.pick, self.currentChoice.sizeIndex)) {
          self.closeModal();
        }
        return;
      }
    });

    var form = this.$('[data-sf-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.submit();
      });
    }
  };

  SizeFinder.prototype.setUnit = function (unit) {
    this.unit = unit;
    var btns = this.root.querySelectorAll('[data-sf-unit]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].classList.toggle('active', btns[i].getAttribute('data-sf-unit') === unit);
    }
    this.root.setAttribute('data-unit', unit);
  };

  SizeFinder.prototype.closeModal = function () {
    var modal = this.root.closest('.modal');
    if (!modal) return;
    /* Prefer the theme's own close control so its handler unbinds cleanly. */
    var close = modal.querySelector('[data-action="close-modal"]');
    if (close) {
      close.click();
    } else {
      modal.classList.add('hidden');
      document.body.classList.remove('modeling');
    }
  };

  SizeFinder.prototype.goToStep = function (step) {
    var steps = this.root.querySelectorAll('[data-sf-step]');
    for (var i = 0; i < steps.length; i++) {
      steps[i].hidden = steps[i].getAttribute('data-sf-step') !== step;
    }
    var label = this.$('[data-sf-series-label]');
    if (label && CHARTS[this.series]) {
      label.textContent = CHARTS[this.series].label + ' sizing';
    }
  };

  SizeFinder.prototype.reset = function () {
    var defaultSeries = this.root.getAttribute('data-default-series') || 'ask';
    this.series = defaultSeries;
    this.setUnit('imperial');
    var err = this.$('[data-sf-error]');
    if (err) err.hidden = true;
    this.goToStep(defaultSeries === 'ask' ? 'series' : 'measure');
  };

  SizeFinder.prototype.readMeasurements = function () {
    var heightIn, weightLb;

    if (this.unit === 'metric') {
      var cm = parseFloat(this.$('[data-sf-height-cm]').value);
      var kg = parseFloat(this.$('[data-sf-weight-kg]').value);
      if (!cm || !kg) return null;
      heightIn = cm / 2.54;
      weightLb = kg * 2.20462;
    } else {
      var ft = parseFloat(this.$('[data-sf-height-ft]').value);
      var inch = parseFloat(this.$('[data-sf-height-in]').value) || 0;
      var lb = parseFloat(this.$('[data-sf-weight-lb]').value);
      if (!ft || !lb) return null;
      heightIn = ft * 12 + inch;
      weightLb = lb;
    }

    if (heightIn < 30 || heightIn > 90) return null;
    if (weightLb < 20 || weightLb > 500) return null;

    return { heightIn: heightIn, weightLb: weightLb };
  };

  SizeFinder.prototype.submit = function () {
    var err = this.$('[data-sf-error]');
    var measurements = this.readMeasurements();

    if (!measurements) {
      if (err) {
        err.textContent = 'Please enter a height and weight so we can place you on the chart.';
        err.hidden = false;
      }
      return;
    }
    if (err) err.hidden = true;

    this.render(recommend(this.series, measurements.heightIn, measurements.weightLb), measurements);
    this.goToStep('result');
  };

  SizeFinder.prototype.renderAvailability = function (sizeCode) {
    var wrap = this.$('[data-sf-availability]');
    var note = this.$('[data-sf-availability-note]');
    var button = this.$('[data-sf-select-variant]');
    if (!wrap) return;

    this.currentChoice = null;

    var product = this.productData();
    if (!product) {
      wrap.hidden = true;
      return;
    }

    /* Nothing to point at — this product may not carry the size, or may not
       use a Size option at all. Say nothing rather than guessing. */
    var choice = chooseVariant(product, sizeCode, this.currentSelections());
    if (!choice) {
      wrap.hidden = true;
      return;
    }

    wrap.hidden = false;

    if (!choice.pick) {
      note.textContent = sizeCode + ' is sold out on this product at the moment.';
      button.hidden = true;
      return;
    }

    this.currentChoice = choice;
    button.hidden = false;
    button.textContent = 'Select ' + sizeCode;

    var parts = [];
    if (choice.status === 'pre-order') {
      parts.push(sizeCode + ' is available to pre-order — it ships when the next batch arrives.');
    } else if (choice.status === 'ships-now') {
      parts.push(sizeCode + ' is in stock and ships right away.');
    } else {
      parts.push(sizeCode + ' is available.');
    }

    /* Be explicit when the only buyable version of this size is in a
       different colour to the one they are looking at. */
    choice.changed.forEach(function (c) {
      parts.push('Not available in ' + c.from + ', so this switches ' +
        c.name.toLowerCase() + ' to ' + c.to + '.');
    });

    note.textContent = parts.join(' ');
  };

  SizeFinder.prototype.render = function (result, measurements) {
    var entry = result.best.entry;

    this.$('[data-sf-result-size]').textContent = entry.size;
    this.$('[data-sf-result-series]').textContent = result.chart.label;

    this.$('[data-sf-result-basis]').textContent =
      'Based on ' + formatHeight(measurements.heightIn) +
      ' and ' + Math.round(measurements.weightLb) + ' lbs.';

    this.$('[data-sf-result-range]').textContent =
      entry.size + ' covers ' + formatHeightRange(entry.h) +
      ' and ' + formatWeightRange(entry.w) + '.';

    var fitNote = this.$('[data-sf-result-fit]');
    fitNote.hidden = !entry.fit;
    if (entry.fit) fitNote.textContent = FIT_NOTES[entry.fit];

    var confidence = this.$('[data-sf-result-confidence]');
    confidence.className = 'sf-confidence sf-confidence--' + result.confidence;
    if (result.confidence === 'strong') {
      confidence.textContent = 'Your height and weight both land inside this size.';
    } else if (result.confidence === 'overlap') {
      confidence.textContent = 'You sit inside two sizes on our chart. We have suggested ' +
        entry.size + ' because you sit closer to the middle of it than to ' +
        result.runnerUp.entry.size + ', which is the other option.';
    } else if (result.confidence === 'close') {
      confidence.textContent = 'You are just outside the printed range, so this is the closest fit rather than an exact one.';
    } else {
      confidence.textContent = 'You fall between sizes on our chart. This is our best read, but it is worth checking with us before ordering.';
    }

    var hint = this.$('[data-sf-result-hint]');
    hint.hidden = !result.sizeUpHint;
    if (result.sizeUpHint) {
      hint.textContent = 'They are near the top of this size — if they are still growing, ' +
        result.sizeUpHint + ' will last longer.';
    }

    this.renderAvailability(entry.size);

    var altWrap = this.$('[data-sf-result-alternates]');
    var altList = this.$('[data-sf-result-alternates-list]');
    altList.innerHTML = '';
    var worthShowing = result.alternates.filter(function (alt) {
      return alt.score.total <= result.best.score.total + 2;
    });
    altWrap.hidden = worthShowing.length === 0;
    worthShowing.forEach(function (alt) {
      var li = document.createElement('li');
      var name = document.createElement('strong');
      name.textContent = alt.entry.size;
      li.appendChild(name);
      var detail = ' — ' + formatHeightRange(alt.entry.h) + ', ' + formatWeightRange(alt.entry.w);
      if (alt.entry.fit) detail += '. ' + FIT_NOTES[alt.entry.fit];
      li.appendChild(document.createTextNode(detail));
      altList.appendChild(li);
    });
  };

  function init() {
    var roots = document.querySelectorAll('[data-size-finder]');
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].hasAttribute('data-sf-ready')) {
        roots[i].setAttribute('data-sf-ready', 'true');
        new SizeFinder(roots[i]);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init after a theme-editor section reload. */
  document.addEventListener('shopify:section:load', init);

  /* Exposed so the recommendation logic can be exercised directly. */
  window.SizeFinderEngine = {
    CHARTS: CHARTS,
    recommend: recommend,
    parseOptionValue: parseOptionValue,
    codeMatches: codeMatches,
    chooseVariant: chooseVariant
  };
})();
